/*
 * Copyright (c) Hilo 2020.
 * Author: fharold
 *
 * hilo-oauth-manager
 * App.tsx
 */

import './sass/hilo-style.scss';

import React from 'react';
import {createBrowserHistory} from "history";
import AuthenticationService, {AuthenticationWatcher} from './tools/service/AuthenticationService';
import NavigationService from './tools/service/NavigationService';
import Container from './Container';
import Translation from "./services/translation/Translation";
import TranslationService, {TranslationListener} from "./tools/translation/TranslationService";
import UserService, {UserListener} from "./services/UserService";
import User from "./models/User";
import Splashscreen from "./tools/view/layout/Splashscreen";
import ServiceRepository from "./tools/service/ServiceRepository";
import ServiceRepositoryHelper from "./services/ServiceRepositoryHelper";
import HTTPService from "./tools/service/HTTPService";

interface State {
    user?: User;
    splashscreenDisplay: boolean;
}

export default class App extends React.Component<{}, State> implements UserListener, AuthenticationWatcher, TranslationListener {
    public static history = createBrowserHistory();
    public state: State;
    public t: Translation;

    private readonly _authenticationService: AuthenticationService;
    private readonly _userService: UserService;
    private readonly _navigationService: NavigationService;
    private readonly _translationService: TranslationService;
    private readonly _httpService: HTTPService;
    private _splashscreen = React.createRef<Splashscreen>();

    constructor(props: {}, state: State) {
        super(props, state);

        const serviceRepository = ServiceRepository.instance;
        ServiceRepositoryHelper.inject(serviceRepository);

        this._authenticationService = serviceRepository.resolve(AuthenticationService.id) as AuthenticationService;
        this._userService = serviceRepository.resolve(UserService.id) as UserService;
        this._navigationService = serviceRepository.resolve(NavigationService.id) as NavigationService;
        this._translationService = serviceRepository.resolve(TranslationService.id) as TranslationService;
        this._httpService = serviceRepository.resolve(HTTPService.id) as HTTPService;

        this.t = this._translationService.translation;

        this.state = {user: undefined, splashscreenDisplay: true};

        this._navigationService.setHistory(App.history);
    }

    /**
     * componentDidMount
     */
    public componentDidMount() {
        this._userService.addListener(this);
        this._authenticationService.addListener(this);
        this._translationService.addListener(this);

        this._startSession();
    }

    /**
     * _startSession
     * @private
     */
    private async _startSession() {
        try {
            await this._authenticationService.initializeSession(this._httpService);
        } catch (e) {
            this.setState({user: undefined, splashscreenDisplay: false});
        }

        this._splashscreen.current?.skip()
    }

    /**
     * componentWillUnmount
     */
    public componentWillUnmount() {
        this._translationService.removeListener(this);
        this._authenticationService.removeListener(this);
        this._userService.removeListener(this);
    }

    /**
     * authenticationRefreshed
     */
    public authenticationRefreshed(): void {

    }

    /**
     * currentUserHasBeenUpdated
     * @param user
     */
    public currentUserHasBeenUpdated(user: User | undefined) {
        this.setState({user: user});
    }

    /**
     * translationUpdated
     * @param t
     */
    public translationUpdated(t: Translation): void {
        this.t = t;
        this.forceUpdate();
    }

    public render(): JSX.Element {
        const {user} = this.state;

        return <Splashscreen ref={this._splashscreen}>
            <Container user={user}/>
        </Splashscreen>
    }
}
